import type { MessageKey } from '@dijitalapp/i18n';
import type { GuardianTutorDto, TutorClassUsageDto, TutorReplyDto, TutorSessionDto, TutorStatusDto } from '@dijitalapp/shared';
import { TUTOR_MAX_INPUT_CHARS } from '@dijitalapp/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';

/** Pedagogical AI tutor UI (ADR-022). Every rule shown here is enforced by the server. */

function useRun() {
  const { i18n } = useApp();
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setError(null);
      try {
        await fn();
      } catch (err) {
        setError(i18n.t(errorMessageKey(err)));
      }
    },
    [i18n],
  );
  return { error, run };
}

// ---------- student ----------

export function StudentTutor({ classId }: { classId: string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const [status, setStatus] = useState<TutorStatusDto | null>(null);
  const [session, setSession] = useState<TutorSessionDto | null>(null);
  const [past, setPast] = useState<Omit<TutorSessionDto, 'messages'>[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    const s = await api<TutorStatusDto>('GET', '/tutor/status');
    setStatus(s);
    const mine = s.classes.find((c) => c.classId === classId);
    if (mine?.usable || mine?.reason === 'quota' || mine?.reason === 'quiz_in_progress') {
      setPast((await api<{ sessions: Omit<TutorSessionDto, 'messages'>[] }>('GET', `/tutor/sessions?classId=${classId}`)).sessions);
    }
  }, [classId]);
  useEffect(() => {
    let alive = true;
    api<TutorStatusDto>('GET', '/tutor/status').then(
      (s) => {
        if (!alive) return;
        setStatus(s);
        if (s.classes.find((c) => c.classId === classId)?.usable) {
          void api<{ sessions: Omit<TutorSessionDto, 'messages'>[] }>('GET', `/tutor/sessions?classId=${classId}`).then((r) => alive && setPast(r.sessions));
        }
      },
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [classId]);

  const mine = status?.classes.find((c) => c.classId === classId);
  // Nothing to show when the platform has no tutor, or the teacher has not switched it on for this class.
  if (!status || !mine || mine.reason === 'unavailable' || mine.reason === 'class_disabled') return null;

  const send = (e: FormEvent) => {
    e.preventDefault();
    if (!session || !text.trim()) return;
    setBusy(true);
    void run(async () => {
      const r = await api<TutorReplyDto>('POST', `/tutor/sessions/${session.id}/messages`, { text });
      setSession({ ...session, turnsLeft: r.turnsLeft, messages: [...session.messages, r.student, r.reply] });
      setNote(r.redacted ? t('tutor.redacted', { count: r.redacted }) : null);
      setText('');
    }).finally(() => setBusy(false));
  };

  return (
    <section className="card stack" aria-labelledby={`tutor-${classId}`}>
      <h3 id={`tutor-${classId}`} style={{ margin: 0 }}>
        🧑‍🏫 {t('tutor.title')}
      </h3>
      <p className="hint">{t('tutor.intro')}</p>
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}

      {mine.reason === 'consent_required' && (
        <div className="stack">
          <ul className="hint" style={{ margin: 0 }}>
            {(['tutor.consent.hints', 'tutor.consent.redaction', 'tutor.consent.retention', 'tutor.consent.visibility'] as MessageKey[]).map((k) => (
              <li key={k}>{t(k)}</li>
            ))}
            {status.minor && <li>{t('tutor.consent.guardian')}</li>}
          </ul>
          <button className="btn btn-primary" type="button" onClick={() => void run(async () => (await api('PUT', '/tutor/consent', { consent: true }), await load()))}>
            {t('tutor.consent.accept')}
          </button>
        </div>
      )}
      {mine.reason && mine.reason !== 'consent_required' && <p role="status">{t(`tutor.reason.${mine.reason}` as MessageKey)}</p>}

      {(mine.usable || mine.reason === 'quota') && !session && (
        <div className="stack">
          {mine.usable && (
            <button className="btn btn-primary" type="button" onClick={() => void run(async () => setSession(await api<TutorSessionDto>('POST', '/tutor/sessions', { classId })))}>
              {t('tutor.newSession')}
            </button>
          )}
          {past.map((p) => (
            <button key={p.id} className="list-item" type="button" onClick={() => void run(async () => setSession(await api<TutorSessionDto>('GET', `/tutor/sessions/${p.id}`)))}>
              <span>{p.topic ?? i18n.formatDate(p.lastAt)}</span>
            </button>
          ))}
          <p className="hint">{t('tutor.remaining', { count: status.remainingToday })}</p>
        </div>
      )}

      {session && (
        <div className="stack">
          <ol className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-live="polite">
            {session.messages.map((m) => (
              <li
                key={m.id}
                className="card"
                style={{
                  alignSelf: m.role === 'student' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  whiteSpace: 'pre-wrap',
                  background: m.role === 'student' ? 'var(--accent-soft)' : m.role === 'safety' ? 'var(--surface-2)' : undefined,
                }}
              >
                {m.role !== 'student' && <strong className="muted">{m.role === 'safety' ? `💛 ${t('tutor.safety')}` : t('tutor.title')}: </strong>}
                {m.body}
              </li>
            ))}
          </ol>
          {busy && <p className="muted">{t('tutor.thinking')}</p>}
          {note && <p className="hint">{note}</p>}
          {mine.usable && session.turnsLeft > 0 ? (
            <form className="stack" onSubmit={send}>
              <label className="visually-hidden" htmlFor={`tq-${classId}`}>
                {t('tutor.ask')}
              </label>
              <textarea id={`tq-${classId}`} className="input" rows={2} maxLength={TUTOR_MAX_INPUT_CHARS} placeholder={t('tutor.ask')} value={text} onChange={(e) => setText(e.target.value)} />
              <div className="row">
                <span className="hint">{t('tutor.turnsLeft', { count: session.turnsLeft })}</span>
                <span className="spacer" />
                <button className="btn" type="button" onClick={() => (setSession(null), void run(load))}>
                  {t('learn.back')}
                </button>
                <button className="btn btn-primary" disabled={busy || !text.trim()}>
                  {t('tutor.send')}
                </button>
              </div>
            </form>
          ) : (
            <button className="btn" type="button" onClick={() => (setSession(null), void run(load))}>
              {t('learn.back')}
            </button>
          )}
        </div>
      )}

      {status.consented && (
        <button className="link-button" type="button" onClick={() => void run(async () => (await api('PUT', '/tutor/consent', { consent: false }), setSession(null), await load()))}>
          {t('tutor.consent.withdraw')}
        </button>
      )}
    </section>
  );
}

// ---------- teacher ----------

export function TeacherTutorSwitch({ classId }: { classId: string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const [usage, setUsage] = useState<TutorClassUsageDto | null>(null);
  useEffect(() => {
    void api<TutorClassUsageDto>('GET', `/classes/${classId}/tutor`).then(setUsage, () => undefined);
  }, [classId]);
  if (!usage) return null;
  return (
    <section className="card stack">
      <label className="check">
        <input
          type="checkbox"
          checked={usage.enabled}
          onChange={(e) => void run(async () => setUsage(await api<TutorClassUsageDto>('PUT', `/classes/${classId}/tutor`, { enabled: e.target.checked })))}
        />
        <span>🧑‍🏫 {t('tutor.teacher.enable')}</span>
      </label>
      <p className="hint">{t('tutor.teacher.note')}</p>
      {usage.enabled && <p className="muted">{t('tutor.teacher.usage', { students: usage.studentsThisWeek, messages: usage.messagesThisWeek })}</p>}
      {error && <p className="alert alert-error">{error}</p>}
    </section>
  );
}

// ---------- guardian ----------

export function GuardianTutorControl({ studentId, initial }: { studentId: string; initial: GuardianTutorDto }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const [state, setState] = useState(initial);
  return (
    <div className="stack" style={{ gap: 'var(--space-1)' }}>
      <h3 style={{ margin: 0 }}>🧑‍🏫 {t('tutor.title')}</h3>
      <label className="check">
        <input
          type="checkbox"
          checked={state.approved}
          onChange={(e) => void run(async () => setState(await api<GuardianTutorDto>('PUT', `/guardian/children/${studentId}/tutor`, { approved: e.target.checked })))}
        />
        <span>{t('tutor.guardian.approve')}</span>
      </label>
      <p className="hint">{t('tutor.guardian.note')}</p>
      {state.approved && <span className="muted">{t('tutor.guardian.usage', { count: state.messagesThisWeek })}</span>}
      {error && <p className="alert alert-error">{error}</p>}
    </div>
  );
}
